import { Controller } from "@hotwired/stimulus";

const arrivalHintDuration = 2500;
const thanksHintDuration = 1700;
const hintFadeOutDuration = 300;
const cardCollapseDuration = 350;
const pickedToCollapseDelay = 480;
const sentToCollapseDelay = 150;

export default class extends Controller {
    static targets = ["hint", "hintText", "card", "option", "other"];
    static values = {
        urlWithoutOrigin: String,
        thanksText: String,
    };

    connect() {
        // A reload must not show the card again and count it as seen twice.
        history.replaceState(history.state, "", this.urlWithoutOriginValue);
        this.showHint(arrivalHintDuration);
    }

    pick(event) {
        if (this.isDone) {
            event.preventDefault();
            return;
        }
        this.isDone = true;
        this.collapseAfter = Date.now() + pickedToCollapseDelay;
        this.select(event.currentTarget);
        this.otherTarget.classList.remove("is-open");
    }

    pickOther(event) {
        if (this.isDone) {
            return;
        }
        this.select(event.currentTarget);
        this.otherTarget.classList.add("is-open");
        const input = this.otherTarget.querySelector("input[name=Other]");
        // Focus inside the click handler, otherwise iOS doesn't open the keyboard.
        input.focus({ preventScroll: true });
        setTimeout(() => {
            const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
            if (input.getBoundingClientRect().bottom > viewportHeight - 12) {
                input.scrollIntoView({ block: "nearest", behavior: "smooth" });
            }
        }, 320);
    }

    send(event) {
        if (this.isDone) {
            event.preventDefault();
            return;
        }
        this.isDone = true;
        this.collapseAfter = Date.now() + sentToCollapseDelay;
    }

    dismiss(event) {
        if (this.isDone) {
            event.preventDefault();
            return;
        }
        this.isDone = true;
        this.collapseCard();
    }

    submitEnd(event) {
        if (!event.detail.success) {
            this.isDone = false;
            return;
        }
        if (!this.collapseAfter) {
            return;
        }
        setTimeout(() => {
            this.collapseCard(() => {
                this.hintTextTarget.textContent = this.thanksTextValue;
                this.showHint(thanksHintDuration);
            });
        }, Math.max(0, this.collapseAfter - Date.now()));
    }

    collapseCard(onCollapsed) {
        this.cardTarget.classList.add("is-invisible");
        setTimeout(() => {
            this.cardTarget.remove();
            if (onCollapsed) {
                onCollapsed();
            }
        }, cardCollapseDuration);
    }

    showHint(duration) {
        const hint = this.hintTarget;
        clearTimeout(this.hintTimeout);
        hint.classList.remove("is-invisible");
        // Restart the pop-in animation if the arrival hint is still visible.
        hint.setAttribute("hidden", "");
        void hint.offsetWidth;
        hint.removeAttribute("hidden");
        this.hintTimeout = setTimeout(() => {
            hint.classList.add("is-invisible");
            this.hintTimeout = setTimeout(() => hint.setAttribute("hidden", ""), hintFadeOutDuration);
        }, duration);
    }

    select(option) {
        this.optionTargets.forEach((o) => o.classList.toggle("is-selected", o === option));
    }
}
